<script setup lang="ts">
import {useAuthor} from "~/composables/useAuthor";
import ProjectComment from "~/components/ProjectComment.vue";
import SupportAuthorCard from "~/components/SupportAuthorCard.vue";
import {formatDate} from "~/utils/format";
import type {ApiError} from "~/types/ApiError";
import {userHasRole} from "~/utils/user";

const {isAuthenticated} = useSanctumAuth();
const appUser = useAppUser();
const isReader = computed(() => !!appUser.value && userHasRole(appUser.value, "reader"));
const route = useRoute();
const router = useRouter();
const authorUuid = route.params.author;
if (typeof authorUuid !== "string") {
  throw createError({
    statusCode: 400,
    statusMessage: "UUID d'auteur invalide",
    fatal: true,
  });
}

// Load composables
const {getAuthor, getProjects, getComments, isSubscribed, toggleSubscription} = useAuthor();

const page = ref(1);
const commentsPage = ref(1);

// Fetch data
const [
  {data: authorResponse, status: authorStatus, error: authorError},
  {data: projectsResponse, status: projectsResponseStatus},
  {data: commentsResponse, status: commentsStatus},
] = await Promise.all([
  getAuthor(authorUuid),
  getProjects(authorUuid, page),
  getComments(authorUuid, commentsPage),
]);

if (authorError.value) {
  const errorData = authorError.value.data as ApiError;
  throw createError({
    statusCode: authorError.value.statusCode || 500,
    statusMessage: errorData?.message || "Auteur non trouvé",
    fatal: true,
  });
}

const author = computed(() => authorResponse.value?.data);

useWreadiaSeo({
  title: () => author.value?.name,
  description: () => author.value?.profiles?.author?.mini_bio || author.value?.profiles?.author?.bio,
  image: () => author.value?.profiles?.author?.picture,
  ogType: "profile",
  extra: {
    profileUsername: () => author.value?.name,
  }
});

if (!author.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Auteur non trouvé",
    fatal: true,
  });
}

const projects = computed(() => projectsResponse.value?.data ?? []);
const projectsMeta = computed(() => projectsResponse.value?.meta ?? null);

const comments = computed(() => commentsResponse.value?.data ?? []);
const commentsMeta = computed(() => commentsResponse.value?.meta ?? null);

// Subscription logic
const isSubscribedToAuthor = ref(false);
const subscriptionLoading = ref(false);

if (isAuthenticated.value) {
  const {data: subscriptionData} = await isSubscribed(authorUuid);
  isSubscribedToAuthor.value = subscriptionData.value?.data.status ?? false;
}

const onSubscriptionToggle = async (value: boolean) => {
  if (subscriptionLoading.value) return;

  subscriptionLoading.value = true;
  try {
    await toggleSubscription(authorUuid);
    // On ne change pas manuellement la valeur ici car le v-model l'a déjà fait
  } catch (e) {
    console.error("Failed to toggle subscription", e);
    // En cas d'erreur, on revient à l'état précédent
    isSubscribedToAuthor.value = !value;
  } finally {
    subscriptionLoading.value = false;
  }
};

const hasPagination = computed(() => !!projectsMeta.value &&
    Number(projectsMeta.value.total) > Number(projectsMeta.value.per_page) &&
    Number(projectsMeta.value.per_page) > 0);

const hasCommentsPagination = computed(() => !!commentsMeta.value &&
    Number(commentsMeta.value.total) > Number(commentsMeta.value.per_page) &&
    Number(commentsMeta.value.per_page) > 0);

const tabs = [
  {
    label: "Histoires",
    value: "projects",
    slot: "projects",
  },
  {
    label: "À propos",
    value: "about",
    slot: "about",
  },
  {
    label: "Derniers retours des lecteurs",
    value: "comments",
    slot: "comments",
  },
];

const selectedTab = computed({
  get() {
    return (route.query.tab as string) || (tabs[0]?.value as string);
  },
  set(value) {
    router.replace({
      query: {
        ...route.query,
        tab: value,
      },
    });
  },
});
</script>

<template>
  <UPage id="pagePage">
    <UPageBody id="pageBody">
      <UContainer>
        <div v-if="authorStatus === 'pending'" class="flex justify-center py-20">
          <UIcon name="i-heroicons-arrow-path" class="animate-spin h-12 w-12"/>
        </div>

        <div v-else-if="author">
          <div class="flex flex-col lg:flex-row gap-8 lg:gap-16">
            <div class="lg:flex-4/12 lg:pr-16 lg:border-r-2 border-gray-100">
              <div class="flex justify-between items-start">
                <UAvatar
                    :src="author.profiles?.author?.picture"
                    :alt="author.name"
                    class="mb-8 w-20 h-20"
                />
                <UButton v-if="appUser && appUser.uuid === author.uuid" color="secondary" variant="solid" size="sm" to="/dashboard/profile">Modifier mon profil</UButton>
              </div>

              <h1 class="text-2xl">
                {{ author.name }}
              </h1>

              <p class="whitespace-pre-wrap lg:mb-6">
                {{ author.profiles?.author?.mini_bio }}
              </p>

              <div v-if="author.profiles?.author?.links && author.profiles.author.links.length > 0" class="mt-2 lg:mt-4">
                <div class="flex flex-wrap gap-3">
                  <UButton
                      v-if="author.profiles.author.website"
                      :to="author.profiles.author.website"
                      target="_blank"
                      icon="i-heroicons-globe-alt"
                      variant="ghost"
                  />
                  <UButton
                      v-for="(link, index) in author.profiles.author.links"
                      :key="index"
                      :to="link.url"
                      target="_blank"
                      size="sm"
                      class="capitalize"
                      variant="ghost"
                      :icon="`i-simple-icons-${link.platform}`"
                  />
                </div>
              </div>

              <div v-if="author.preferred_genres && author.preferred_genres.length > 0" class="mt-8">
                <ul class="flex gap-x-4">
                  <li v-for="genre in author.preferred_genres">
                    <BadgeGenre :genre="genre"/>
                  </li>
                </ul>
              </div>

              <USeparator class="my-6"></USeparator>

              <div class="flex lg:flex-col justify-between mb-8 space-y-2">
                <div class="flex flex-col md:flex-row gap-x-1">
                  <span class="flex flex-nowrap gap-x-2 items-center">
                    <UIcon name="i-custom-eye" class="size-5"/>
                    <span class="font-semibold">{{ author.readers_count }}</span>
                  </span>
                  <span>{{ pluralize(author.readers_count || 0, "lecteur", "lecteurs") }}</span>
                </div>
                <div class="flex flex-col md:flex-row gap-x-1">
                  <span class="flex flex-nowrap gap-x-2 items-center">
                    <UIcon name="i-custom-book-pile" class="size-5"/>
                    <span class="font-semibold">{{ author.projects_count }}</span>
                  </span>
                  <span>{{ pluralize(author.projects_count || 0, "histoire", "histoires") }}</span>
                </div>
                <div class="flex flex-col md:flex-row gap-x-1">
                  <span class="flex flex-nowrap gap-x-2 items-center">
                    <UIcon name="i-custom-puzzle" class="size-5"/>
                    <span class="font-semibold">{{ author.chapters_count }}</span>
                  </span>
                  <span>{{ pluralize(author.chapters_count || 0, "chapitre", "chapitres") }}</span>
                </div>
                <div class="flex flex-col md:flex-row gap-x-1">
                  <span class="flex flex-nowrap gap-x-2 items-center">
                    <UIcon name="i-custom-comment" class="size-5"/>
                    <span class="font-semibold">{{ author.author_comments_count }}</span>
                  </span>
                  <span>{{ pluralize(author.author_comments_count || 0, "commentaire", "commentaires") }}</span>
                </div>
              </div>

              <SupportAuthorCard :author="author" v-if="appUser && userHasRole(appUser, 'reader')"/>

              <div v-if="isReader" class="mt-8">
                <UFormField label="Être informé·e par mail des nouvelles publications de l’auteur.e">
                  <USwitch
                      v-model="isSubscribedToAuthor"
                      :loading="subscriptionLoading"
                      @update:model-value="onSubscriptionToggle"
                  />
                </UFormField>
              </div>
            </div>

            <div class="lg:flex-8/12">
              <UTabs v-model="selectedTab" :items="tabs" variant="link">
                <template #projects>
                  <div v-if="projectsResponseStatus === 'pending' && !projects.length" class="flex justify-center">
                    <UIcon name="i-heroicons-arrow-path" class="animate-spin"/>
                  </div>
                  <div v-else-if="projects.length > 0">
                    <div class="flex flex-col md:grid grid-cols-2 gap-8 lg:gap-16">
                      <ProjectCard v-for="project in projects" :project="project" responsiveLayout="detail"/>
                    </div>

                    <div v-if="projectsMeta && hasPagination" class="flex justify-center">
                      <UPagination
                          v-model:page="page"
                          :total="projectsMeta.total"
                          :items-per-page="projectsMeta.per_page"
                      />
                    </div>
                  </div>
                  <div v-else-if="projects.length === 0 ">
                    Cet auteur n'a pas encore publié d'histoire.
                  </div>
                </template>
                <template #about>
                  <div v-html="nl2br(author?.profiles?.author?.bio || '')"></div>
                </template>
                <template #comments>
                  <div v-if="commentsStatus === 'pending' && !comments.length" class="flex justify-center">
                    <UIcon name="i-heroicons-arrow-path" class="animate-spin"/>
                  </div>
                  <div v-else-if="comments.length > 0" class="flex flex-col gap-4">
                    <ProjectComment
                        v-for="comment in comments"
                        :key="comment.id"
                        :comment="comment"
                        show-context
                    />

                    <div v-if="commentsMeta && hasCommentsPagination" class="flex justify-center">
                      <UPagination
                          v-model:page="commentsPage"
                          :total="commentsMeta.total"
                          :items-per-page="commentsMeta.per_page"
                      />
                    </div>
                  </div>
                  <p v-else-if="commentsStatus !== 'pending'">
                    Aucun commentaire public pour le moment.
                  </p>
                </template>
              </UTabs>
            </div>
          </div>
        </div>
      </UContainer>
    </UPageBody>
  </UPage>
</template>
